import random from 'lodash-es/random'
import { ThunkAction } from 'redux-thunk'

import { IRootState } from '../../common/reducers'
import { IExperimentsConfig } from '../../config/experiments'
import {
  reportExperimentEnrolment,
  reportExperimentsDimensions
} from '../analytics/experimentsAnalytics'
import { Consent, userHasOptedOutOfCookiesForCategory } from '../utils/consent'
import { getExperimentCookie, setExperimentCookie } from '../utils/cookieHelper'
import { hasFeature } from '../utils/features'
import {
  isExperimentRunning,
  isExperimentIndependent,
  hasFulfilledExperimentDependency
} from '../utils/experiments'
import weightedRandom from '../utils/weightedRandom'

export enum TypeKeys {
  EXPERIMENT_DATA_RECEIVED = 'experimentDataReceived',
  EXPERIMENTS_READY = 'experimentsReady',
  EXPERIMENTS_ERROR = 'fetchExperimentDataError'
}

interface IExperimentPayload {
  experiment_id: string
  variant: string
}

interface IExperimentDataReceivedAction {
  type: TypeKeys.EXPERIMENT_DATA_RECEIVED
  payload: IExperimentPayload
}

interface IExperimentsReadyAction {
  type: TypeKeys.EXPERIMENTS_READY
  payload: boolean
}

interface IExperimentsErrorAction {
  type: TypeKeys.EXPERIMENTS_ERROR
  payload: IExperimentPayload
}

export type ActionTypes =
  | IExperimentDataReceivedAction
  | IExperimentsReadyAction
  | IExperimentsErrorAction

export const experimentsReady = (): IExperimentsReadyAction => ({
  type: TypeKeys.EXPERIMENTS_READY,
  payload: true
})

export const getExperimentVariant = (
  experimentsConfig: IExperimentsConfig,
  experimentName: string
): IExperimentDataReceivedAction => {
  const experimentConfig = experimentsConfig[experimentName]
  const { experimentId, variantsWeights, variants } = experimentConfig
  const variantsKeys = Object.keys(variants)
  const experimentVariantsCount = variantsKeys.length
  const experimentCookieValue = getExperimentCookie({
    experimentId,
    consentType: Consent.preferences
  })

  // default value
  const experimentData: IExperimentPayload = {
    experiment_id: experimentId,
    variant: '0'
  }

  // only start to read/write cookie when user hasn't optedout
  if (!userHasOptedOutOfCookiesForCategory(Consent.preferences)) {
    // if the experiment cookie exist, simply fetch it from cookie
    if (experimentCookieValue) {
      experimentData.variant = experimentCookieValue

      // otherwise generate a random variant and save it to cookie
    } else {
      const randomVariant = variantsWeights
        ? weightedRandom(variantsKeys, variantsWeights).toString()
        : random(experimentVariantsCount - 1).toString()
      experimentData.variant = randomVariant
      setExperimentCookie({
        experimentId,
        itemValue: randomVariant,
        consentType: Consent.preferences,
        cookieOptions: {
          expires: 90, // 90 days
          path: '/'
        }
      })
    }
  }

  try {
    reportExperimentEnrolment({
      experimentId,
      variant: experimentData.variant
    })
  } finally {
    // eslint-disable-next-line
    return { type: TypeKeys.EXPERIMENT_DATA_RECEIVED, payload: experimentData }
  }
}

export const setExperiments = (
  experimentsConfig: IExperimentsConfig
): ThunkAction<
  IExperimentDataReceivedAction | IExperimentsReadyAction | null,
  IRootState,
  void
> => {
  return (dispatch, getState) => {
    const { currentSite } = getState().common
    const { location } = getState().router

    // First set independent experiments
    Object.keys(experimentsConfig).forEach(experimentName => {
      if (
        isExperimentRunning({
          experimentsConfig,
          experimentName,
          currentSite,
          location
        }) &&
        isExperimentIndependent(experimentsConfig, experimentName)
      ) {
        dispatch(getExperimentVariant(experimentsConfig, experimentName))
      }
    })

    // Then set experiments dependent on other experiment enrolment
    Object.keys(experimentsConfig).forEach(experimentName => {
      if (
        isExperimentRunning({
          experimentsConfig,
          experimentName,
          currentSite,
          location
        }) &&
        !isExperimentIndependent(experimentsConfig, experimentName) &&
        hasFulfilledExperimentDependency(experimentsConfig, experimentName)
      ) {
        dispatch(getExperimentVariant(experimentsConfig, experimentName))
      }
    })

    const experiments = getState().experiments

    if (
      hasFeature('experiment:dimensions', currentSite, location, experiments)
    ) {
      reportExperimentsDimensions(experiments)
    }

    return dispatch(experimentsReady())
  }
}
